import React, { useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { ContentCopy, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useGetApiCredentialsHandler } from '../dataHandlers/getApiCredentialsHandler';
import {
  useGenerateOTPHandler,
  useRegenerateSecretHandler,
  useViewSecretHandler,
} from '../dataHandlers/mutateApiIntegrationHandler';
import { apiSettingsCardSx } from '../../../constants/apiSettingsCardTypography';
import NewSecretModal from '../../../components/NewSecretModal';
import OTPModal from '../../../components/OTPModal';

const APIIntegration = () => {
  const dispatch = useDispatch();

  const [revealedSecret, setRevealedSecret] = useState('');
  const [newSecret, setNewSecret] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [newSecretModalOpen, setNewSecretModalOpen] = useState(false);
  const [otpAction, setOtpAction] = useState('');

  const { credentials, isLoading: isCredentialsLoading } = useGetApiCredentialsHandler();

  const { onSubmit: generateOTP, isPending: isGeneratingOTP } = useGenerateOTPHandler({});

  const { onSubmit: viewSecret, isPending: isViewingSecret } = useViewSecretHandler({
    onSuccess: (data) => {
      setRevealedSecret(data.client_secret);
      setOtpModalOpen(false);
    },
  });

  const { onSubmit: regenerateSecret, isPending: isRegeneratingSecret } = useRegenerateSecretHandler({
    onSuccess: (data) => {
      setNewSecret(data.client_secret);
      setNewSecretModalOpen(true);
      setOtpModalOpen(false);
      setRevealedSecret('');
    },
  });

  const isLoading = isCredentialsLoading || isGeneratingOTP || isViewingSecret || isRegeneratingSecret;

  const handleViewSecret = () => {
    setOtpAction('view_secret');
    setOtpModalOpen(true);
  };

  const handleRegenerateSecret = () => {
    setOtpAction('regenerate_secret');
    setOtpModalOpen(true);
    setRevealedSecret('');
  };

  const handleHideSecret = () => {
    setRevealedSecret('');
  };

  const handleCloseNewSecretModal = () => {
    setNewSecretModalOpen(false);
    setNewSecret('');
    setRevealedSecret('');
  };

  const handleOTPSubmit = (otp) => {
    if (otpAction === 'view_secret') {
      viewSecret(otp);
    } else if (otpAction === 'regenerate_secret') {
      regenerateSecret(otp);
    }
  };

  const handleGenerateOTP = () => {
    generateOTP(otpAction);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(
        showSnackbar({
          message: `${label} has been copied to clipboard`,
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Failed to copy to clipboard',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box sx={apiSettingsCardSx.container}>
      <Typography sx={apiSettingsCardSx.title}>API Configuration</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ ...apiSettingsCardSx.description, mb: 3 }}>
        Manage your API keys and integration settings
      </Typography>

      <Stack spacing={1.5}>
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={apiSettingsCardSx.label}>
              Client ID
            </Typography>
            <TextField
              fullWidth
              value={credentials?.client_id}
              InputProps={{
                readOnly: true,
                sx: apiSettingsCardSx.input,
              }}
              variant="outlined"
              size="small"
            />
            <IconButton
              onClick={() => copyToClipboard(credentials?.client_id, 'Client ID')}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'black',
                borderRadius: 1,
                minWidth: 36,
                height: 36,
                '&:hover': {
                  borderColor: 'grey.200',
                },
              }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
            <Typography variant="body2" sx={apiSettingsCardSx.label}>
              Client Secret
            </Typography>
            <TextField
              fullWidth
              value={revealedSecret || (credentials ? '••••••••••••••••••••••••••••••••' : '')}
              type={revealedSecret ? 'text' : 'password'}
              InputProps={{
                readOnly: true,
                sx: apiSettingsCardSx.input,
              }}
              variant="outlined"
              size="small"
            />
            {revealedSecret && (
              <IconButton
                onClick={() => copyToClipboard(revealedSecret, 'Client Secret')}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'black',
                  borderRadius: 1,
                  minWidth: 36,
                  height: 36,
                  '&:hover': {
                    borderColor: 'grey.200',
                  },
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Stack direction="row" spacing={1.5} marginTop={2}>
            {revealedSecret ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityOff fontSize="small" />}
                onClick={handleHideSecret}
                disabled={isLoading}
                sx={apiSettingsCardSx.button}
              >
                Hide Secret
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={<Visibility fontSize="small" />}
                onClick={handleViewSecret}
                disabled={isLoading || !!revealedSecret}
                sx={apiSettingsCardSx.button}
              >
                {revealedSecret ? 'Secret Visible' : 'View Secret'}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh fontSize="small" />}
              onClick={handleRegenerateSecret}
              disabled={isLoading}
              sx={apiSettingsCardSx.button}
            >
              Regenerate Secret
            </Button>
          </Stack>
        </Box>
      </Stack>

      <OTPModal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          // Hide revealed secret if user was viewing it and closes modal
          if (otpAction === 'view_secret') {
            setRevealedSecret('');
          }
        }}
        onSubmit={handleOTPSubmit}
        onGenerateOTP={handleGenerateOTP}
        action={otpAction}
        isLoading={isLoading}
      />

      <NewSecretModal open={newSecretModalOpen} onClose={handleCloseNewSecretModal} secret={newSecret} />
    </Box>
  );
};

export default APIIntegration;
